import { Module } from '../../student/types';

/**
 * Legacy module state logic used by the old CourseScreen.
 *
 * Logic:
 * - If module is submitted or graded, return that state.
 * - Otherwise, return 'active' if the module is among the first 3
 *   available modules for its primary course.
 * - Otherwise, return 'locked'.
 *
 * @param module - The module to check
 * @param modules - All available modules for the student
 * @returns The UI state: 'submitted' | 'graded' | 'active' | 'locked'
 */
export const getModuleState = (module: Module, modules: Module[]): string => {
  const workflowState = module.state?.studentWorkflowState;

  // 1. Terminal states are always returned as is
  if (workflowState === 'submitted' || workflowState === 'graded') {
    return workflowState;
  }

  // 2. Filter modules that are not yet finished
  const availModules = modules.filter(
    (m) => m.state?.studentWorkflowState !== 'submitted' && m.state?.studentWorkflowState !== 'graded',
  );

  // 3. Group by primary course and check if it's in the first 3
  const courseIds = availModules.map((m) => m.courses?.[0]?.id).filter(Boolean);
  const distinctCourseIds = [...new Set(courseIds)];

  for (const courseId of distinctCourseIds) {
    const courseModules = availModules.filter((m) => m.courses?.[0]?.id === courseId);
    // Note: Legacy logic relied on module.state.sortIndex
    // In new models, sortIndex is in courseSettings. We check both.
    const sortedModules = courseModules.sort((a, b) => {
      // @ts-expect-error - legacy state object had sortIndex directly on it
      const sortA = a.courseSettings?.sortIndex ?? a.state?.sortIndex ?? 0;
      // @ts-expect-error - legacy state object had sortIndex directly on it
      const sortB = b.courseSettings?.sortIndex ?? b.state?.sortIndex ?? 0;
      return sortA - sortB;
    });
    const moduleIndex = sortedModules.findIndex((m) => m.id === module.id);

    if (moduleIndex !== -1 && moduleIndex < 3) {
      return 'active';
    }
  }

  return 'locked';
};

export default getModuleState;
